import without from "lodash-es/without";
import type { DataBucket } from "viewer/model/bucket_data_handling/bucket";
import type PullQueue from "viewer/model/bucket_data_handling/pullqueue";
import type PushQueue from "viewer/model/bucket_data_handling/pushqueue";

class TemporalBucketManager {
  // Manages temporal buckets (i.e., buckets created for annotation where
  // the original bucket has not arrived from the server yet) and handles
  // their special treatment.
  pullQueue: PullQueue;
  pushQueue: PushQueue;
  loadedPromises: Array<Promise<void>>;

  constructor(pullQueue: PullQueue, pushQueue: PushQueue) {
    this.pullQueue = pullQueue;
    this.pushQueue = pushQueue;
    this.loadedPromises = [];
  }

  getCount(): number {
    return this.loadedPromises.length;
  }

  addBucket(bucket: DataBucket): void {
    this.pullBucket(bucket);
    this.loadedPromises.push(this.makeLoadedPromise(bucket));
  }

  pullBucket(bucket: DataBucket): void {
    bucket.addToPullQueueWithHighestPriority();
    this.pullQueue.pull();
  }

  makeLoadedPromise(bucket: DataBucket): Promise<void> {
    const loadedPromise: Promise<void> = new Promise((resolve, _reject) => {
      const onLoadedOrMissingHandler = () => {
        if (bucket.dirty) {
          this.pushQueue.insert(bucket);
        }

        this.loadedPromises = without(this.loadedPromises, loadedPromise);
        return resolve();
      };

      // No "bucketRequestFailed" here: These buckets are dirty, so the pull queue retries
      // them until the real backend data is read and merged (resolving as empty could lose data).
      bucket.on("bucketLoaded", onLoadedOrMissingHandler);
      bucket.on("bucketMissing", onLoadedOrMissingHandler);
    });
    return loadedPromise;
  }

  getAllLoadedPromise = async () => {
    await Promise.all(this.loadedPromises);
  };
}

export default TemporalBucketManager;
